import { Injectable } from '@angular/core';
import { select, Store } from '@ngrx/store';
import { Observable } from 'rxjs';
import { filter, map, take, tap } from 'rxjs/operators';
import {
  Business,
  Program,
  ProgramTypes,
  Role,
  RoleTypes,
  User,
  UserRole,
} from '../models/user-role.model';
import { UserActions, UserSelectors, UserStore } from '../store';
import { IUserProfileFormModel } from '@carm/features/private/accounts-and-profiles/features/user-profile/models/user-profile-form.model';
import { IErrorCode } from '@carm/_utils/models/error.model';

@Injectable()
export class UserService {
  private userRole: UserRole;
  private nativeRoles: Role[];
  private user: User;

  constructor(private store: Store<UserStore.State>) {
    this.store.pipe(select(UserSelectors.getUserRoles)).subscribe(userRole => {
      this.userRole = userRole;
    });

    this.store
      .pipe(select(UserSelectors.getNativeRoles))
      .subscribe(nativeRoles => {
        this.nativeRoles = nativeRoles;
      });

    this.store
      .pipe(select(UserSelectors.getUserInformation))
      .subscribe(userInformation => {
        this.userRole = userInformation?.role;
        this.user = userInformation?.user;
      });
  }

  /**
   * Returns the user business Id
   */
  getUserBusinessId(): Observable<string> {
    return this.store.pipe(
      select(UserSelectors.getBusiness),
      map(business => (business ? business.uid : null))
    );
  }

  /**
   * Gets curent program type
   * @returns curent program type
   */
  getLastUsedProgramType(): Observable<ProgramTypes> {
    return this.store.pipe(select(UserSelectors.getLastUsedProgramType));
  }

  /**
   * Gets the user current role
   * @returns user current role
   */
  getUserLastUsedRole(): Observable<Role> {
    return this.store.pipe(select(UserSelectors.getLastUsedRole));
  }

  /**
   * Gets current selected business Uid and Program Uid
   * @returns businessUid and ProgramUid
   */
  getLastUsedBusinessAndProgramUid(): Observable<{businessUid: string, programUid: string}> {
    return this.store.pipe(select(UserSelectors.getLastUsedBusinessProgramUid));
  }

  /**
   * Gets the business ProgramTypes list
   * @returns programTypes Array
   */
  getCurrentBusinessPrograms(): Observable<ProgramTypes[]> {
    return this.store.pipe(select(UserSelectors.getBusinessProgramTypes));
  }

  /**
   * Gets the selected business id
   * @returns user last used select business id
   */
  getLastUsedSelectedBusinessId(): Observable<string> {
    return this.store.pipe(select(UserSelectors.getLastUsedRoleBusinessUid));
  }

  /**
   * Gets the user current program
   * @returns user current program
   */
   getUserCurrentProgramId(): Observable<string> {
    return this.store.pipe(select(UserSelectors.getCurentProgramUid));
  }

  /**
   * Gets the user current role
   * @returns user current role
   */
  getNativeRoles(): Observable<Role[]> {
    return this.store.pipe(select(UserSelectors.getNativeRoles));
  }

  /**
   * Gets the user current role
   * @returns user current role
   */
  getUserInformation(): Observable<{ user: User; role: UserRole }> {
    return this.store.pipe(select(UserSelectors.getUserInformation));
  }

  /**
   * Gets user current role type
   * @returns user current role type
   */
  getUserLastUsedRoleType(): Observable<RoleTypes> {
    return this.store.pipe(select(UserSelectors.getLastUsedRoleType));
  }

  /**
   * Gets user roles
   * @returns user roles
   */
  getUserRoles(): Observable<UserRole> {
    return this.store.pipe(select(UserSelectors.getUserRoles));
  }

  updateUserRoles(data: { user: User, role: UserRole}): void {
    if ( this.isNativeBusiness(data.role)) {
        this.store.dispatch(UserActions.loadNativeRolesSuccess({ data: { roles: data.role.roles} }));
    } else if ( data.role && data.role.business) {
        this.store.dispatch(UserActions.loadNativeRoles({ data: { businessId: data.role.business.uid} }));
    }

    if ( !(data.role && data.role.lastUsedRole) ){
      data.role = this.getOrphanCurrentRole(); //get orphan current role in case of no roles
    }
    return this.store.dispatch(UserActions.loadRolesSuccess({ data }));
  }

  /**
   * Gets user roles sync
   * @returns user roles
   */
  getUserRolesSync(): UserRole {
    return this.userRole;
  }

  /**
   * Gets user roles sync
   * @returns user roles
   */
  getUserNativeRolesSync(): Role[] {
    return this.nativeRoles;
  }

  /**
   * Changes current business and program by uid and reload roles
   * @param businessId
   */
  selectNewBusinessProgramByUid(businessId: string, programId: string) {
    this.store.dispatch(
      UserActions.loadRoleByBusinessProgram({ data: { businessId, programId } })
    );
  }

  isRoleLoaded(): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.selectState), tap(state=>{
      console.log('isRoleLoaded Call');
    }),
    filter(state=> state.loadingBusiness !== undefined && state.loadingBusiness === false),
    map(state=> state.businessLoadSuccess))
  }

  /**
   * Gets user sync
   * @returns user
   */
  getUserSync(): User {
    return this.user;
  }

  setIsDefaultAddress(formData: IUserProfileFormModel): Observable<boolean> {
    this.store.dispatch(
      UserActions.updateIsDefaultAddressExist({
        defaultExists: true,
        data: formData,
      })
    );
    return this.store.pipe(select(UserSelectors.isUserProfileExist));
  }

  /**
   * Gets user business
   * @returns user business
   */
  getUserBusiness(): Observable<Business> {
    return this.store.pipe(select(UserSelectors.getBusiness));
  }

  /**
   * Changes current business by uid and reload roles
   * @param businessId
   */
  changeLastUsedBusinessByUid(businessId: string) {
    this.store.dispatch(
      UserActions.loadRoleByBusiness({ data: { businessId } })
    );
  }

  /**
   * Get 1st Native role for the particular program
   * @param inProgramType
   * @returns user native role by program
   */
  getUserNativeRoleByProgram(inProgramType: ProgramTypes): Observable<Role> {
    return this.getUserRoles().pipe(
      map(userRoles =>
        userRoles
          ? userRoles.roles.filter(
              role =>
                role.isNative && role.program.programType === inProgramType
            )
          : []
      ),
      map(nativeRoles =>
        nativeRoles && nativeRoles.length > 0 ? nativeRoles[0] : null
      )
    );
  }

  /**
   * Gets user program sorted list
   * @returns user program list
   */
  getUserCurrentRoleProgramList(): Observable<Program[]> {
    return this.getUserRoles().pipe(
      map(userRole => {
        return userRole && userRole.roles
          ? userRole.roles
              .map(r => r.program)
              .sort((a, b) => (a.name < b.name ? -1 : 1))
          : [];
      })
    );
  }

  /**
   * Gets user Native program sorted list
   * @returns user native program list
   */
  getUserNativeProgramList(): Observable<Program[]> {
    return this.getNativeRoles().pipe(
      map(nativeRoles => {
        return nativeRoles
          ? nativeRoles
              .map(r => r.program)
              .sort((a, b) => (a.name < b.name ? -1 : 1))
          : [];
      })
    );
  }

  /**
   * Gets user Native program type
   * @returns user native program list
   */
  getUserNativeProgramType(): Observable<ProgramTypes> {
    return this.getNativeRoles().pipe(
      map(nativeRoles => {
        return nativeRoles && nativeRoles.length > 0
          ? nativeRoles[0].program.programType
          : null;
      })
    );
  }

  /**
   * Gets user fetch error
   * @returns user current role
   */
  getFetchError(): Observable<IErrorCode> {
    return this.store.pipe(select(UserSelectors.getError));
  }

  showProgramSwitchMenu(): Observable<boolean> {
    return this.store.pipe(select(UserSelectors.showProgramSwitchMenu));
  }

  toggleProgramSwitchMenu(showMenu: boolean) {
    this.store.dispatch(UserActions.toggleProgramMenu({ data: showMenu }));
  }

  getUserLastLoggedLogout(): Observable<Date> {
    return this.store
      .pipe(select(UserSelectors.getUserInformation))
      .pipe(map(userInfo => userInfo?.user?.lastLogout));
  }

  clearUserInformation() {
    this.store.dispatch(UserActions.clearUserInformation());
  }

  isSelectedBusinessNative(): Observable<boolean> {
    return this.store
      .pipe(select(UserSelectors.getLastUsedRole))
      .pipe(map(role => role?.isNative));
  }

  isUserIsBroker(): Observable<boolean> {
    return this.getUserNativeProgramType().pipe(
      map(ProgramType => ProgramType === ProgramTypes.CustomsBroker)
    );
  }

  isUserImporter(): Observable<boolean> {
    return this.getLastUsedProgramType().pipe(
      take(1),
      map((programType: ProgramTypes) => {
        return (
          programType === ProgramTypes.Importer ||
          programType === ProgramTypes.ImporterR1
        );
      })
    );
  }

  logOut() {
    this.store.dispatch(UserActions.clearUserInformation());
    localStorage.clear();
    this.userRole = null;
    this.nativeRoles = null;
    this.user = null;
  }

  setAcceptTermsAndConditions() {
    this.store.dispatch(UserActions.setTermConditionAccepted());
  }

  getAcceptTermsAndConditions() {
    return this.store.pipe(
      select(UserSelectors.getAcceptTermsAndConditions)
    );
  }

  setAcceptTermsAndConditionsAPI() {
    this.store.dispatch(UserActions.setTermConditionAPIAccepted());
  }

  getAcceptTermsAndConditionsAPI() {
    return this.store.pipe(
      select(UserSelectors.getAcceptTermsAndConditionsAPI)
    );
  }

  isOrphanUser(): Observable<boolean> {
    return this.getUserInformation()
    .pipe(
      take(1),
      map(userInfo => (userInfo?.role?.lastUsedRole?.roleType === RoleTypes.Orphan && userInfo?.role?.roles?.length === 0))
    );
  }

  private isNativeBusiness(userRole: UserRole): boolean {
    if ( userRole && userRole.business && userRole.roles){
        return userRole.roles.findIndex( (role) => role.program.companyUid === userRole.business.uid) > -1;
    }
    return false;
  }

  private getOrphanCurrentRole(): UserRole{
    return {
      roles: [],
      business: null,
      lastUsedRole: {
          roleType: RoleTypes.Orphan,
          roleName: 'Orphan',
          isNative: false,
          program: {
            uid: null,
            programType: ProgramTypes.ALL, // for orphan
        }
      }
    }
  }
}
